import React, { useState, useMemo, useEffect } from 'react'
import { Modal } from 'lib/modal'
import { useQuery } from 'jsonapi-react'
import { calculate, minimumDefaults } from 'app/theme/calculator'
import { steps, postHogTrackingConfig, trackerSteps } from './form/config'
import LeadHeader from './form/LeadHeader'
import LeadFooter from './form/LeadFooter'
import useEventTracker from 'lib/useEventTracker'
import useMetaEventTracker, {
  types as metaTypes,
  standardEvents as metaStandardEvents 
} from 'lib/useMetaEventTracker'
import useLinkedInEventTracker from '../../lib/useLinkedInEventTracker'
import { useFeatureFlags } from 'app/utils/feature-flags'

export default function LeadFormModal(props) {
  const {
    isOpen,
    closeModal,
    reloadExperiments,
    posthog,
    experiments,
    recording,
    initialData = {},
    sessionData,
    setSessionData,
    data,
    setData,
    getInitialData,
    ...rest
  } = props

  const tracker = useEventTracker()
  const metaTrackerStandard = useMetaEventTracker()
  const metaTrackerCustom = useMetaEventTracker({functionName: metaTypes.custom})
  const linkedInTracker = useLinkedInEventTracker()
  const [featureFlags] = useFeatureFlags()
  const [minimum, setMinimum] = useState()
  const [estimate, setEstimate] = useState()
  const [amountOpened, setAmountOpened] = useState(0)
  const firstStepNumber = 0
  const [step, setStep] = useState(steps[firstStepNumber])
  const [stepNumber, setStepNumber] = useState(firstStepNumber)
  const [errors, setErrors] = useState()
  const [highestLandedStep, setHighestLandedStep] = useState(0)
  const stepIndex = steps.findIndex(s => s === step)

  const {
    data: occasionData,
    isLoading: occasionsIsLoading,
    client
  } = useQuery(['occasions', {
        page: { size: 99 },
  }], {
      ssr: false,
      cacheTime: 60 * 10,
  })

  /* Prefetch Occasions */
  const occasions = useMemo(() => {
    if (!occasionData) {
      return []
    }
    return occasionData.filter(o => {
        if (data.recurring) {
          return o.groups.find(g => g === 'recurring')
        }
        return data.payer === 'host'
          ? o.groups.find(g => g === 'catering')
          : o.groups.find(g => g === 'public_events')
    }).sort((a,b) => {
        if (a.code === 'other') {
          return 1
        } else if (b.code === 'other') {
          return -1
        } else if (a.position === null) {
          return 1
        } else if (b.position === null) {
          return -1
        } else {
          return Number(a.position) - Number(b.position)
        }
      })
  }, [occasionData, data.payer, data.recurring])

  /* Prefetch Countries*/
  const countryQuery = useQuery(['countries', {
      sort: 'name',
      include: 'regions',
      page: {
        size: 999,
      },
    sort: [
      'name',
      'regions.name',
    ]
  }])

  const [countries, country, regions] = useMemo(() => {
    const countries = countryQuery.data || []
    const country = countries.find(c => c.code === data.address?.country_code)
    const regions = country?.regions || []

    return [countries, country, regions]
  }, [countryQuery.data, data.address?.country_code])

  /* Get Catering Minimums */
  useEffect(() => {
    if (!data.address) {
      return
    }

    let filter = null

    if (data.address.location) {
      filter = {
        location: [data.address.location.lng, data.address.location.lat],
      }
    }
    if (!filter && data.address.zip && data.address.zip.length >= 5) {
      filter = {
        zip: [data.address?.formatted, data.address.zip].filter(Boolean),
      }
    }

    if (!filter) {
      return
    }

    let conditions = {
      sort: 'distance',
      filter: filter,
      extra_fields: {
        catering_minimums: 'distance'
      },
      page: {
        size: 1,
      },
    }

    client.fetch(['catering_minimums', conditions]).then(res => {
      if (res.data?.length) {
        const match = res.data.find(min => min.display_name.includes('Jacksonville, FL') || min.display_name.includes('Cleveland, OH'))
        if (match) {
          setMinimum(null)
          return
        }
        setMinimum(res.data[0])
      } else {
        setMinimum(null)
      }
    })
  }, [data.address])

  /* Get Catering Estimate */
  useEffect(() => {
    if (!minimum) {
      return
    }
    setEstimate(calculate({
        foods: data.foods,
        eaters: data.eaters,
        catering: minimum.catering_min || minimumDefaults.catering,
        dessert: minimum.dessert_min || minimumDefaults.dessert,
        cateringTagPage: data.meta?.page === 'tag',
    }))
  }, [minimum, data.foods])

  // Filter steps based on enabled status
  const enabledSteps = steps.filter(s => 
    !s.isEnabled || s.isEnabled(data, experiments)
  )

  const prevStep = () => {
    setStepNumber(stepNumber-1)

    const index = enabledSteps.findIndex(s => s === step)

    // Get the previous step directly from the array
    const prev = index > 0 ? enabledSteps[index - 1] : null

    linkedInTracker.event(trackerSteps[step.name].getTracked(step.name, data, false))
    if (prev) {
      setErrors(null)
      setStep(prev)
    }
  }

  const nextStep = () => {
    const validationResults = step.validate(data, country)
    if (validationResults) {
      setErrors(validationResults)
      return
    }

    setStepNumber(stepNumber+1)
    const index = enabledSteps.findIndex(s => s === step)


    if (step.name === 'payer') {
      // Reset the highest validated step when the user changes their payer (which changes form steps)
      setHighestLandedStep(0)
    }

    // Check if there's a next step available
    const hasNextStep = index < enabledSteps.length - 1
    const next = hasNextStep ? enabledSteps[index + 1] : null

    if (step.label) {
      tracker.event('Next', {
        event_category: 'Lead Form',
        event_label: step.label,
      })
    }

    if (step.name === 'contact') {
      metaTrackerStandard.event(
        metaStandardEvents.contact,
        trackerSteps[step.name].getTracked(step.name, data)
      )
    }
    else {
      metaTrackerCustom.event(
        'View',
        trackerSteps[step.name].getTracked(step.name, data)
      )
    }

    linkedInTracker.event(trackerSteps[step.name].getTracked(step.name, data))

    const postHogEvent = postHogTrackingConfig[step.name]

    if (postHogEvent) {
      posthog.capture(
        postHogEvent.event_slug,
        {
          ...postHogEvent.fields(
            stepNumber,
            data
          ),
        ...data.meta,
        ...sessionData.meta,
        url: window.location.pathname,
        }
      )
    }
    if (step.name === 'contact') {
      submit()
    } else if (next) {
      setStep(next)

      if(index >= highestLandedStep) {
        // Needed to track the furthest the user has progressed in the form
        setHighestLandedStep(index + 1)
      }
    }
  }

  const submit = () => {
    setStep(steps.find(s => s.name === 'loading'))

    tracker.event('Submit', {
      event_category: 'Lead Form',
    })

    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        email: data.contact?.email,
        phone: data.contact?.phone,
      })
    }

    linkedInTracker.event(trackerSteps[step.name].getTracked(step.name, data))

    metaTrackerCustom.event(
      'Submit',
      trackerSteps[step.name].getTracked(step.name, data)
    )

    client.mutate(['leads', {
      extra_fields: { leads: 'vip' }
    }], {
          ...data,
          meta: {
            ...data.meta,
            ...sessionData.meta,
            url: window.location.pathname,
            posthog_experiments: {
              ...data.meta?.posthog_experiments,
          ...experiments
        }
          },
          contact: {
            ...data.contact,
            _method: 'create',
          },
    }, {
          invalidate: false,
    }).then(res => {
        if (res.data) {
          setData(prev => ({
            ...prev,
          status: res.data.status
          }))

          setSessionData(prev => ({
            ...prev,
            contact: res.data.contact,
            last_submitted: Date.now(),
          }))

          setHighestLandedStep(0)

        const groups = (res.data.vip ? ['vip'] : []).concat(
          data.occasion?.groups?.filter(v => v !== 'catering' && v !== 'public_events')
        ).filter(Boolean).join(',')

          const eaters_range = res.data.recurring ? 100 : 2000

          const event_label = [
            ['payer', data.payer],
            ['occasion', data.occasion?.code],
            ['groups', groups],
            ['recurring', data.frequency],
          ['eaters', data.eaters > eaters_range ? `${eaters_range}+` : `0-${eaters_range}` ],
        ].filter(arr =>
          arr[1]
        ).map(arr =>
          `${arr[0]}:${arr[1]}`
        ).join(' ')

          // Always fire PostHog and identification (these don't depend on DOM)
          posthog.capture(
            postHogTrackingConfig['success'].event_slug,
            postHogTrackingConfig['success'].fields(stepNumber, {
              ...data,
              ...sessionData.meta,
              lead_id: res.data.id,
              url: window.location.pathname,
            })
          )

          posthog.identify(res.data.contact.id, {
            email: res.data.contact.email,
            name: `${res.data.contact.first_name} ${res.data.contact.last_name}`,
          })

          // Handle different success flows
          if (res.data.instant_book) {
            // For instant book: Fire GTM tracking immediately before redirect
            // GTM will have time to fire before page unload
            tracker.event('Success', {
              event_category: 'Lead Form',
              event_label: event_label,
              payer: data.payer,
              occasion: data.occasion?.id,
            })

            metaTrackerStandard.event(
              metaStandardEvents.lead,
              trackerSteps['success'].getTracked('success', data)
            )

            linkedInTracker.event(
              trackerSteps['success'].getTracked('success', data)
            )

            // Small delay to ensure tracking fires before redirect
            setTimeout(() => {
              window.location.href = `/book-now/${res.data.token}/`
            }, 100)
          } else {
            // For normal success page: Store tracking data and fire after DOM is ready
            setData(prev => ({
              ...prev,
              successTrackingData: {
                event_label,
                lead_id: res.data.id,
                trackerData: trackerSteps['success'].getTracked(
                  'success',
                  data
                ),
              },
            }))

            setStep(steps.find(s => s.name === 'success'))
          }

        } else {
          setStep(steps.find(s => s.name === 'fail'))

          tracker.event('Error', {
            event_category: 'Lead Form',
          })

          metaTrackerCustom.event('Error', {
            event_category: 'Lead Form',
            event_step: step.name
          })
        }
      })

    setAmountOpened(0)
  }

  const closeAndReset = () => {
    closeModal()

    if (step.name === 'success' || step.name === 'fail') {
      setStep(steps[firstStepNumber])
    }
    if (step.name === 'success') {
      setData(getInitialData())
    }
  }

  const closeAndTrack = () => {
    const stepLabel = step.label

    posthog.capture(`[LeadForm][${stepLabel}] closed`, {
      url: window.location.pathname,
      group: `[LeadForm][${stepLabel}]`
    })

    metaTrackerCustom.event(`Close`, {
      event_category: 'Lead Form',
      event_step: step.name,
    })

    closeAndReset()
  }

  /* Track Lead Form Open in PostHog and Meta */
  useEffect(() => {
    if (isOpen) {
      tracker.event('View', {
        event_category: 'Lead Form',
      })

      if(stepNumber === firstStepNumber && amountOpened === 0) {
        metaTrackerCustom.event('Open', {
          event_category: 'Lead Form',
          event_step: step.name,
        })

        posthog.capture(
          'Lead Form Started',
          {
          //prior_page_type: 'test', // High LOE, leaving this out for now
          prior_page_url: window.location.href, // this is fine for now
          // prior_page_title: 'test', // High LOE, leaving this out for now
          //cta_label: 'Get Started' // High LOE, leaving this out for now
          ...sessionData.meta,
          ...data.meta,
          url: window.location.pathname,
          }
        )
      }
      else {
        metaTrackerCustom.event('Reopen', {
          event_category: 'Lead Form',
          event_step: step.name,
        })
      }

      setAmountOpened(prev => prev + 1)
      recording.start()
    } else {
      recording.stop()
    }
  }, [isOpen])

  /* Fire delayed success tracking when success step is reached and DOM is ready */
  useEffect(() => {
    if (step.name === 'success' && data.successTrackingData) {
      // Small delay to ensure GTM hidden fields are in DOM
      setTimeout(() => {
        tracker.event('Success', {
          event_category: 'Lead Form',
          event_label: data.successTrackingData.event_label,
          payer: data.payer,
          occasion: data.occasion?.id,
        })

        metaTrackerStandard.event(
          metaStandardEvents.lead,
          data.successTrackingData.trackerData
        )

        linkedInTracker.event(data.successTrackingData.trackerData)

        // Clear the tracking data to prevent duplicate firing
        setData(prev => ({
          ...prev,
          successTrackingData: null,
        }))
      }, 50) // Small delay to ensure DOM is ready
    }
  }, [step.name, data.successTrackingData])

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeAndTrack}
      className='relative w-full shadow-lg self-start max-w-full'
    >
      <div
        className='flex flex-col w-full min-w-screen max-w-screen'
        style={{
          height: '100dvh'
        }}
      >
        {!step.hide_header && (
          <LeadHeader
            closeAction={closeAndTrack}
            highestLandedStep={highestLandedStep || 0}
            data={data}
            featureFlags={featureFlags}
            steps={enabledSteps.filter(s => {
                if (s.name === 'loading' || s.name === 'fail' || s.name === 'success') {
                return false
              } else if (data.payer === 'host' && ( ['recurrence', 'event', 'min_guarantee'].includes(s.name))) {
                return false
              } else if (data.payer === 'attendee' && ( ['foods', 'budget'].includes(s.name))) {
                return false
              } else if (s.isEnabled && s.isEnabled(data, experiments) === false) {
                return false
              } else {
                return true
              }
            })}
            step={step}
            setStep={setStep}
          />
        )}
        <div className={[
            'w-full max-w-full flex flex-1 flex-col items-center',
            'overflow-y-auto overflow-x-hidden self-center',
          step.hide_header ? 'md:mt-8' : 'bg-gray-100'
        ]}>
          <div className="w-full max-w-2xl">
            {step.heading && (
              <div className="text-2xl font-bold text-gray-800 text-center py-3 max-w-5xl">
                {step.heading}
              </div>
            )}
            <step.component
              prevStep={prevStep}
              nextStep={nextStep}
              steps={enabledSteps}
              errors={errors}
              setErrors={setErrors}
              data={data}
              setData={setData}
              occasions={occasions}
              occasionsIsLoading={occasionsIsLoading}
              countries={countries}
              country={country}
              regions={regions}
              estimate={estimate}
              minimum={minimum}
              step={step}
              catererName={props.catererName}
              closeModal={closeAndReset}
              featureFlags={featureFlags}
              setHighestLandedStep={setHighestLandedStep}
              highestLandedStep={highestLandedStep}
              experiments={experiments}
              posthog={posthog}
              reloadExperiments={reloadExperiments}
            />
          </div>
        </div>
        {!step.hide_footer && (
          <LeadFooter
            prevStep={prevStep}
            nextStep={nextStep}
            stepName={step.name}
          />
        )}
      </div>
    </Modal>
  )
}
